/**
 * UserManagement — CRUD de users sobre /api/admin/users.
 *
 * Operaciones:
 *  - GET /api/admin/users → lista.
 *  - POST /api/admin/users → crear (email, name, user_type).
 *  - PATCH /api/admin/users/:id → editar (name, user_type, is_active).
 *  - DELETE /api/admin/users/:id → eliminar.
 *
 * El header X-User-Email se inyecta vía apiHelpers (instalado en AuthContext).
 * 403 → mensaje "No tenés permisos".
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE } from '../config';
import './UserManagement.css';

const DEFAULT_ROLES = ['user', 'admin', 'superadmin'];

function fmtDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch (_) {
    return '—';
  }
}

function RoleBadge({ role }) {
  const cls = role === 'superadmin' ? 'um-role-superadmin'
    : role === 'admin' ? 'um-role-admin'
    : 'um-role-user';
  return <span className={`um-role-pill ${cls}`}>{role || 'user'}</span>;
}

function ToastBanner({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);

  if (!toast) return null;
  return (
    <div className={`um-toast ${toast.type === 'error' ? 'um-toast-error' : ''}`}>
      {toast.message}
    </div>
  );
}

function UserModal({ mode, initial, validRoles, onClose, onSubmit, busy }) {
  const [email, setEmail] = useState(initial?.email || '');
  const [name, setName] = useState(initial?.name || '');
  const [userType, setUserType] = useState(initial?.user_type || 'user');
  const [isActive, setIsActive] = useState(initial?.is_active !== false);
  const [formError, setFormError] = useState(null);

  const isEdit = mode === 'edit';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!isEdit && !email.trim()) {
      setFormError('El email es requerido');
      return;
    }
    if (!name.trim()) {
      setFormError('El nombre es requerido');
      return;
    }
    const payload = isEdit
      ? { name: name.trim(), user_type: userType, is_active: isActive }
      : { email: email.trim(), name: name.trim(), user_type: userType };
    try {
      await onSubmit(payload);
    } catch (err) {
      setFormError(err.message || 'Error al guardar');
    }
  };

  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="um-modal-header">
            <h3>{isEdit ? 'Editar usuario' : 'Nuevo usuario'}</h3>
          </div>
          <div className="um-modal-body">
            {formError && <div className="um-error" style={{ marginBottom: 12 }}>{formError}</div>}

            <div className="um-field">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isEdit}
                placeholder="usuario@dominio.com"
                required={!isEdit}
              />
            </div>

            <div className="um-field">
              <label>Nombre</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre completo"
                required
              />
            </div>

            <div className="um-field">
              <label>Rol</label>
              <select value={userType} onChange={(e) => setUserType(e.target.value)}>
                {(validRoles || DEFAULT_ROLES).map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            {isEdit && (
              <div className="um-field um-checkbox-row">
                <input
                  id="um-active"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <label htmlFor="um-active">Usuario activo</label>
              </div>
            )}
          </div>
          <div className="um-modal-footer">
            <button type="button" className="um-btn um-btn-ghost" onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button type="submit" className="um-btn um-btn-primary" disabled={busy}>
              {busy ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear usuario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteModal({ user, onClose, onConfirm, busy }) {
  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal" onClick={(e) => e.stopPropagation()}>
        <div className="um-modal-header">
          <h3>Eliminar usuario</h3>
        </div>
        <div className="um-modal-body">
          <p>
            Vas a eliminar a <strong>{user?.email}</strong>.
            Esta acción no se puede deshacer.
          </p>
          <div className="um-warning">
            Si era el último superadmin, el backend va a rechazar la operación.
          </div>
        </div>
        <div className="um-modal-footer">
          <button className="um-btn um-btn-ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="um-btn um-btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Eliminando...' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UserManagement({ currentRole }) {
  const [users, setUsers] = useState([]);
  const [validRoles, setValidRoles] = useState(DEFAULT_ROLES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');

  const [modal, setModal] = useState({ mode: null, user: null, busy: false });
  const [deleteState, setDeleteState] = useState({ user: null, busy: false });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`);
      if (res.status === 403) {
        throw new Error('No tenés permisos para ver usuarios (se requiere superadmin).');
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setUsers(Array.isArray(data.data) ? data.data : []);
      if (Array.isArray(data.valid_roles) && data.valid_roles.length > 0) {
        setValidRoles(data.valid_roles);
      }
    } catch (err) {
      console.error('[UserManagement] fetch error:', err);
      setError(err.message || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => {
      return (
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.user_type && u.user_type.toLowerCase().includes(q))
      );
    });
  }, [users, search]);

  const handleCreate = async (payload) => {
    setModal(m => ({ ...m, busy: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 403) throw new Error('No tenés permisos para crear usuarios.');
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      setModal({ mode: null, user: null, busy: false });
      showToast(`Usuario ${payload.email} creado`);
      await fetchUsers();
    } catch (err) {
      setModal(m => ({ ...m, busy: false }));
      throw err;
    }
  };

  const handleUpdate = async (payload) => {
    const user = modal.user;
    if (!user) return;
    setModal(m => ({ ...m, busy: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(user.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 403) throw new Error('No tenés permisos para editar usuarios.');
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      setModal({ mode: null, user: null, busy: false });
      showToast(`Usuario ${user.email} actualizado`);
      await fetchUsers();
    } catch (err) {
      setModal(m => ({ ...m, busy: false }));
      throw err;
    }
  };

  const handleDelete = async () => {
    const user = deleteState.user;
    if (!user) return;
    setDeleteState(s => ({ ...s, busy: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(user.id)}`, {
        method: 'DELETE',
      });
      if (res.status === 403) {
        showToast('No tenés permisos para eliminar usuarios.', 'error');
      } else if (!res.ok) {
        const j = await res.json().catch(() => null);
        showToast(j?.error || `HTTP ${res.status}`, 'error');
      } else {
        showToast(`Usuario ${user.email} eliminado`);
        await fetchUsers();
      }
    } catch (err) {
      showToast(err.message || 'Error al eliminar', 'error');
    } finally {
      setDeleteState({ user: null, busy: false });
    }
  };

  if (currentRole !== 'superadmin') {
    return (
      <div className="um-card">
        <div className="um-empty">
          La gestión de usuarios está disponible solo para superadmins.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="um-card">
        <div className="um-toolbar">
          <div className="um-toolbar-left">
            <input
              className="um-search"
              type="search"
              placeholder="Buscar por email, nombre o rol..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="um-count">
              {loading ? '...' : `${filtered.length} de ${users.length}`}
            </span>
          </div>
          <div>
            <button
              className="um-btn um-btn-ghost"
              onClick={fetchUsers}
              disabled={loading}
              style={{ marginRight: 8 }}
            >
              Refrescar
            </button>
            <button
              className="um-btn um-btn-primary"
              onClick={() => setModal({ mode: 'create', user: null, busy: false })}
            >
              + Nuevo usuario
            </button>
          </div>
        </div>

        {error && <div className="um-error" style={{ margin: 14 }}>{error}</div>}

        <div className="um-table-wrap">
          {loading ? (
            <div className="um-loading">Cargando usuarios...</div>
          ) : filtered.length === 0 ? (
            <div className="um-empty">
              {users.length === 0 ? 'No hay usuarios cargados.' : 'Sin resultados para la búsqueda.'}
            </div>
          ) : (
            <table className="um-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Nombre</th>
                  <th>Rol</th>
                  <th>Activo</th>
                  <th>Creado</th>
                  <th style={{ textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id || u.email}>
                    <td>{u.email}</td>
                    <td>{u.name || '—'}</td>
                    <td><RoleBadge role={u.user_type} /></td>
                    <td>
                      {u.is_active === false
                        ? <span className="um-active-no">No</span>
                        : <span className="um-active-yes">Sí</span>}
                    </td>
                    <td>{fmtDate(u.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="um-btn um-btn-link"
                        onClick={() => setModal({ mode: 'edit', user: u, busy: false })}
                      >
                        Editar
                      </button>
                      <button
                        className="um-btn um-btn-link-danger"
                        onClick={() => setDeleteState({ user: u, busy: false })}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal.mode && (
        <UserModal
          mode={modal.mode}
          initial={modal.user}
          validRoles={validRoles}
          busy={modal.busy}
          onClose={() => modal.busy ? null : setModal({ mode: null, user: null, busy: false })}
          onSubmit={modal.mode === 'create' ? handleCreate : handleUpdate}
        />
      )}

      {deleteState.user && (
        <DeleteModal
          user={deleteState.user}
          busy={deleteState.busy}
          onClose={() => deleteState.busy ? null : setDeleteState({ user: null, busy: false })}
          onConfirm={handleDelete}
        />
      )}

      <ToastBanner toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
